import { useQuery, useMutation } from '@tanstack/react-query'
import { useCategoriesStore } from '@/store'
import { CategoriesFilterParams, CreateCategoryParams, UpdateCategoryParams } from '@/types/category'
import { BulkIdParams } from '@/types/general'
import { SortOrderEnum } from '@/enums'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useCategories = (params: CategoriesFilterParams = {}) => {
  const fetchCategories = useCategoriesStore(state => state.fetchCategories)

  return useQuery({
    queryKey: ['categories', params],
    queryFn: () => fetchCategories(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000 // 10 minutes
  })
}

export const useCategoriesTree = () => {
  const fetchCategoriesTree = useCategoriesStore(state => state.fetchCategoriesTree)

  return useQuery({
    queryKey: ['categories-tree'],
    queryFn: () => fetchCategoriesTree(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000 // 10 minutes
  })
}

export const useCategory = (categoryId: string) => {
  const getCategory = useCategoriesStore(state => state.getCategory)

  return useQuery({
    queryKey: ['category', categoryId],
    queryFn: () => getCategory(categoryId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!categoryId
  })
}

export const useCreateCategory = () => {
  const createCategory = useCategoriesStore(state => state.createCategory)

  return useMutation({
    mutationFn: (params: CreateCategoryParams) => createCategory(params),
    onSuccess: () => {},
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}

export const useUpdateCategory = () => {
  const updateCategory = useCategoriesStore(state => state.updateCategory)

  return useMutation({
    mutationFn: ({ categoryId, params }: { categoryId: string; params: UpdateCategoryParams }) =>
      updateCategory(categoryId, params),
    onSuccess: () => {},
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}

export const useDeleteCategory = () => {
  const deleteCategory = useCategoriesStore(state => state.deleteCategory)

  return useMutation({
    mutationFn: (categoryId: string) => deleteCategory(categoryId),
    onSuccess: () => {},
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}

export const useBulkDeleteCategories = () => {
  const bulkDeleteCategories = useCategoriesStore(state => state.bulkDeleteCategories)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (params: BulkIdParams) => bulkDeleteCategories(params),
    onSuccess: response => {
      handleBulkDeleteSuccess(response)
    },
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}

export const useSortCategory = () => {
  const sortCategory = useCategoriesStore(state => state.sortCategory)

  return useMutation({
    mutationFn: ({ categoryId, type }: { categoryId: string; type: SortOrderEnum }) => sortCategory(categoryId, type),
    onSuccess: () => {},
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}
